import { useCallback, useMemo } from 'react';
import config from '@config/index';
import { useVendor } from '@context/VendorContext';
import type { NavigationItemProps } from '@goodfynd/layout';
import { useOrganizationHasFeatureAccess } from '@hooks/useFeatureAccess';
import { usePermissions } from '@hooks/usePermissions';
import { getDashboardRoutePath } from 'utils/routes-util';

import routes from '../../../config/routes';

export function useVendorNavItems(): NavigationItemProps[] {
  const { isFetching, vendorId } = useVendor();

  const { isManager } = usePermissions();
  const hasEventLeadsAccess = useOrganizationHasFeatureAccess(
    config.features.eventLeads,
    config.features.eventLeadsLegacy
  );
  const hasCateringLeadsAccess = useOrganizationHasFeatureAccess(
    config.features.cateringLeads
  );
  const hasOnlineOrderingAccess = useOrganizationHasFeatureAccess(
    config.features.onlineOrdering
  );
  const hasPosAccess = useOrganizationHasFeatureAccess(config.features.pos);
  const hasQuotesAccess = useOrganizationHasFeatureAccess(
    config.features.quotes
  );
  const hasVenueLeadsAccess = useOrganizationHasFeatureAccess(
    config.features.venueLeadsFoodTrucks,
    config.features.venueLeadsCatering
  );
  const hasWebsiteAccess = useOrganizationHasFeatureAccess(
    config.features.website
  );

  const isBusy = useMemo(() => isFetching, [isFetching]);

  const createNavItem = useCallback(
    (item: NavigationItemProps) => {
      return {
        ...item,
        isBusy,
      };
    },
    [isBusy]
  );

  return useMemo(
    () => [
      createNavItem({
        href: vendorId
          ? getDashboardRoutePath(routes.dashboard.overview, vendorId)
          : getDashboardRoutePath(routes.dashboard.overview, '~'),
        icon: 'home',
        label: 'Overview',
      }),
      createNavItem({
        icon: 'calendar',
        label: 'Schedule',
        subItems: [
          createNavItem({
            href: getDashboardRoutePath(
              routes.dashboard.schedule.home,
              vendorId
            ),
            label: 'List',
          }),
          createNavItem({
            href: getDashboardRoutePath(
              routes.dashboard.schedule.map,
              vendorId
            ),
            label: 'Map',
          }),
        ],
      }),
      createNavItem({
        icon: 'events_ai',
        iconRight: isManager ? undefined : 'hide',
        label: 'Events',
        subItems: [
          createNavItem({
            href: getDashboardRoutePath(routes.dashboard.events.home, vendorId),
            icon: 'location_pin',
            label: 'Premium events',
          }),
          createNavItem({
            href: getDashboardRoutePath(
              routes.dashboard.events.cateringLeads,
              vendorId
            ),
            isBlocked: isFetching ? undefined : !hasCateringLeadsAccess,
            label: 'Catering events',
          }),
          createNavItem({
            href: getDashboardRoutePath(
              routes.dashboard.events.leads,
              vendorId
            ),
            isBlocked: isFetching ? undefined : !hasEventLeadsAccess,
            label: 'Food truck events',
          }),
          createNavItem({
            href: getDashboardRoutePath(
              routes.dashboard.events.venueLeads,
              vendorId
            ),
            isBlocked: isFetching ? undefined : !hasVenueLeadsAccess,
            label: 'Venue leads',
          }),
        ],
      }),
      createNavItem({
        id: 'transactions',
        icon: 'order',
        label: 'Orders',
        subItems: [
          createNavItem({
            isBlocked: isFetching ? undefined : !hasOnlineOrderingAccess,
            href: getDashboardRoutePath(
              routes.dashboard.transactions.online,
              vendorId
            ),
            label: 'Online',
          }),
          createNavItem({
            isBlocked: isFetching ? undefined : !hasPosAccess,
            href: getDashboardRoutePath(
              routes.dashboard.transactions.walkups,
              vendorId
            ),
            label: 'Walkups',
          }),
          createNavItem({
            href: getDashboardRoutePath(
              routes.dashboard.transactions.all,
              vendorId
            ),
            label: 'All',
          }),
        ],
      }),
      createNavItem({
        icon: 'invoice',
        label: 'Invoicing',
        subItems: [
          createNavItem({
            href: getDashboardRoutePath(
              routes.dashboard.invoicing.quotes,
              vendorId
            ),
            isBlocked: isFetching ? undefined : !hasQuotesAccess,
            label: 'Quotes',
          }),
          createNavItem({
            href: getDashboardRoutePath(
              routes.dashboard.invoicing.invoices,
              vendorId
            ),
            label: 'Invoices',
          }),
        ],
      }),
      createNavItem({
        href: getDashboardRoutePath(routes.dashboard.products.home, vendorId),
        icon: 'library',
        label: 'Catalog',
      }),
      createNavItem({
        icon: 'reports',
        iconRight: isManager ? undefined : 'hide',
        label: 'Reports',
        subItems: [
          createNavItem({
            href: getDashboardRoutePath(
              routes.dashboard.reports.transactions.tickets,
              vendorId
            ),
            label: 'Transactions',
          }),
          createNavItem({
            href: getDashboardRoutePath(
              routes.dashboard.reports.payouts,
              vendorId
            ),
            label: 'Payouts',
          }),
          createNavItem({
            href: getDashboardRoutePath(
              routes.dashboard.reports.advanced,
              vendorId
            ),
            label: 'Summary',
          }),
        ],
      }),
      createNavItem({
        icon: 'person_circled',
        label: 'Account',
        subItems: [
          createNavItem({
            href: getDashboardRoutePath(
              routes.dashboard.account.info.home,
              vendorId
            ),
            label: 'Business info',
          }),
          createNavItem({
            href: getDashboardRoutePath(
              routes.dashboard.account.hardware,
              vendorId
            ),
            label: 'Hardware',
          }),
          createNavItem({
            href: getDashboardRoutePath(
              routes.dashboard.account.subscriptions,
              vendorId
            ),
            label: 'Subscriptions',
          }),
          createNavItem({
            href: getDashboardRoutePath(
              routes.dashboard.account.billing,
              vendorId
            ),
            label: 'Billing',
          }),
        ],
      }),
      createNavItem({
        icon: 'settings',
        iconRight: isManager ? undefined : 'hide',
        label: 'Settings',
        subItems: [
          createNavItem({
            href: getDashboardRoutePath(
              routes.dashboard.settings.website,
              vendorId
            ),
            isBlocked: isFetching ? undefined : !hasWebsiteAccess,
            label: 'Website',
          }),
          createNavItem({
            href: getDashboardRoutePath(
              routes.dashboard.settings.ordering,
              vendorId
            ),
            label: 'Ordering',
          }),
        ],
      }),
      // {
      //   href: getDashboardRoutePath(
      //     routes.support.home,
      //     vendorId,
      //     isFetchingOrganization
      //   ),
      //   icon: 'help',
      //   label: 'Support',
      // },
    ],
    [
      createNavItem,
      hasCateringLeadsAccess,
      hasEventLeadsAccess,
      hasOnlineOrderingAccess,
      hasPosAccess,
      hasQuotesAccess,
      hasVenueLeadsAccess,
      hasWebsiteAccess,
      isFetching,
      isManager,
      vendorId,
    ]
  );
}
